import React, { useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { observer } from 'mobx-react-lite';
import ReactGA from 'react-ga';
import { useStores, useFocus, useSearchInput } from 'hooks';
import { Icon } from 'components/project/ClerkScreen';
import { clerkColors } from 'shared/utils/theme';
import NoResultsSVG from './NoResultsSvg';
import {
  FilterButton,
  FilterButtonContainer,
  Item,
  ItemData,
  ItemList,
  ItemListContainer,
  ItemSearch,
  ItemTitle,
  LinkButton,
  NoResults,
  NoResultsTip,
  NoResultsTitle,
  SearchIcon,
  SearchInput,
  SearchInputCont,
  SearchSpinner,
  SectionTitle,
} from './Styles';

const UserList = ({ onCloseWindow }) => {
  const {
    AuctionStore: { laneClientList: data },
    BidStore: { auction },
    WebSocketStore: { sendFocusSidePanelSearchMessage },
  } = useStores();

  const searchChangeDataMatcher = (searchData, searchTerm) => searchData.filter(
    (item) => item.memberName.toUpperCase().indexOf(searchTerm.toUpperCase()) > -1,
  );

  const filterClickDataMatcher = (filterData, filterValue) => {
    if (filterValue === null) return filterData;
    return filterData.filter((item) => item.viewOnly === filterValue);
  };

  const {
    setIsLoading,
    isLoading,
    isSearchTermEmpty,
    filteredData,
    events: { handleFilterClick, handleSearchChange },
  } = useSearchInput(searchChangeDataMatcher, { data, filterClickDataMatcher });

  const [searchInputRef, setSearchInputRefFocus] = useFocus();

  useEffect(() => {
    if (auction.focusOnSidePanelSearch) {
      setSearchInputRefFocus();
    }

    setIsLoading(false);
  }, [auction.focusOnSidePanelSearch, data, setIsLoading, setSearchInputRefFocus]);

  const filterButtonMappings = [
    {
      bgColor: '#e7e7e7',
      bgVariance: -20,
      color: '#000000',
      icon: 'times',
      text: '',
      viewOnly: null,
    },
    {
      bgColor: '#008000',
      bgVariance: 20,
      color: '#FFFFFF',
      icon: 'user',
      text: 'Bidding Enabled',
      viewOnly: false,
    },
    {
      bgColor: '#FF0000',
      bgVariance: 50,
      color: '#FFFFFF',
      icon: 'user-times',
      text: 'Bidding Disabled',
      viewOnly: true,
    },
  ];

  const userListMappings = [
    {
      bgColor: 'white',
      color: 'black',
      icon: 'user',
      statusName: 'canBid',
      viewOnly: false,
    },
    {
      bgColor: 'white',
      color: '#DB2828',
      hoverVariance: 50,
      icon: 'user-times',
      statusName: 'viewOnly',
      viewOnly: true,
    },
  ];

  return (
    <ItemSearch>
      <SearchInputCont>
        <SearchInput
          autoFocus
          placeholder="Search lane bidders by name/number"
          ref={searchInputRef}
          onChange={handleSearchChange}
          onFocus={() => sendFocusSidePanelSearchMessage()}
        />
        <SearchIcon type="search" size={22} />
        {isLoading && <SearchSpinner />}
      </SearchInputCont>

      {filterButtonMappings && (
        <FilterButtons
          mappings={filterButtonMappings}
          onClick={handleFilterClick}
        />
      )}

      {!isLoading && filteredData.length > 0 && (
        <>
          <SectionTitle>
            Users (
            {filteredData.length}
            )
          </SectionTitle>
          <ItemListContainer>
            <ItemList>
              <RenderItem
                data={filteredData}
                colors={clerkColors}
                mappings={userListMappings}
                onCloseWindow={onCloseWindow}
              />
            </ItemList>
          </ItemListContainer>
        </>
      )}

      {!isSearchTermEmpty && !isLoading && filteredData.length === 0 && (
        <NoResults>
          <NoResultsSVG />
          <NoResultsTitle>
            We couldn&apos;t find anything matching your search
          </NoResultsTitle>
          <NoResultsTip>Try again with a different search string.</NoResultsTip>
        </NoResults>
      )}

      {isSearchTermEmpty && filteredData.length === 0 && (
        <NoResults>
          <NoResultsSVG />
          <NoResultsTitle>Users have not yet joined this lane.</NoResultsTitle>
          <NoResultsTip>
            No need to refresh, users will start to appear as they join the
            lane.
          </NoResultsTip>
        </NoResults>
      )}
    </ItemSearch>
  );
};

UserList.propTypes = {
  onCloseWindow: PropTypes.func.isRequired,
};

export const FilterButtons = ({ mappings, onClick }) => (
  <FilterButtonContainer>
    {mappings.map((item, index) => (
      <FilterButton
        key={index}
        iconOnly
        data-testid={`filter-btn-${item.viewOnly}`}
        bgColor={item.bgColor}
        color={item.color}
        bgVariance={item.bgVariance}
        onClick={() => onClick(item.viewOnly)}
      >
        {item.icon && <Icon icon={item.icon} />}
        {item?.text}
      </FilterButton>
    ))}
  </FilterButtonContainer>
);


FilterButtons.propTypes = {
  mappings: PropTypes.arrayOf(PropTypes.shape({
    bgColor: PropTypes.string.isRequired,
    bgVariance: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })).isRequired,
  onClick: PropTypes.func.isRequired,
};

const RenderItem = ({
  data, colors, mappings, onCloseWindow,
}) => {
  const {
    AuctionStore: { auctionLaneId },
    MessageStore: { handleSetDirectMessageUser },
    UIStore: { handleBidderStatusClick },
    WebSocketStore: { sendDirectMessageToUserMessage },
  } = useStores();

  const getAttributesForItem = (viewOnly) => {
    const mapping = mappings.filter((item) => item.viewOnly === viewOnly)[0];

    return {
      icon: mapping.icon,
      bgColor: colors[mapping.bgColor],
      color: colors[mapping.color],
      hoverVariance: mapping.hoverVariance,
    };
  };

  const handleSendMessageClick = (e, bidderNumber, member, memberName) => {
    e.preventDefault();
    handleSetDirectMessageUser(member, bidderNumber, null, memberName);
    sendDirectMessageToUserMessage();

    ReactGA.event({
      category: 'Messaging',
      action: `Lane ID: ${auctionLaneId}; Member: ${member}; Member Name: ${memberName}; Bidder Number: ${bidderNumber}`,
      label: 'Send Indv Message from Side Panel',
      nonInteraction: false,
    });

    onCloseWindow();
  };

  return data.map((item, index) => {
    const {
      color, bgColor, hoverVariance,
    } = getAttributesForItem(
      item.viewOnly,
    );

    return (
      <Item
        color={color}
        bgColor={bgColor}
        hoverVariance={hoverVariance}
        key={index}
      >
        <BidderStatusButton
          iconOnly
          viewOnly={item.viewOnly}
          onClick={(e) => handleBidderStatusClick(e, auctionLaneId, item.account, item.member, item.viewOnly)}
        />
        <BidderSendMessageButton
          iconOnly
          onClick={(e) => handleSendMessageClick(e, item.account, item.member, item.memberName)}
        />
        <ItemData>
          <ItemTitle color={color}>
            {item.memberName}
          </ItemTitle>
        </ItemData>
      </Item>
    );
  });
};

const BidderStatusButton = ({ viewOnly, onClick }) => (
  <>
    {!viewOnly ? (
      <LinkButton bgColor="#FF0000" color="#FFFFFF" onClick={onClick}>
        <Icon icon="user-times" iconOnly size={1.5} />
      </LinkButton>
    ) : (
      <LinkButton bgColor="#008800" color="#FFFFFF" onClick={onClick}>
        <Icon icon="user-plus" iconOnly size={1.5} />
      </LinkButton>
    )}
  </>
);

BidderStatusButton.propTypes = {
  viewOnly: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const BidderSendMessageButton = ({ onClick }) => (
  <LinkButton bgColor="#0095E8" color="#FFFFFF" onClick={onClick}>
    <Icon icon="paper-plane" iconOnly size={1.5} />
  </LinkButton>
);

BidderSendMessageButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

export default observer(UserList);
